import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  setDoc,
} from "firebase/firestore";
import {
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Snackbar,
  Avatar,
  Chip,
  Switch,
  Grid,
  Paper,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Link,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PublicIcon from "@mui/icons-material/Public";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import LanguageIcon from "@mui/icons-material/Language";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ImageIcon from "@mui/icons-material/Image";
import EditIcon from "@mui/icons-material/Edit";
import VerifiedIcon from "@mui/icons-material/Verified";
import WarningIcon from "@mui/icons-material/Warning";
import CommentIcon from "@mui/icons-material/Comment";
import { db } from "../firebase";
import UserTable from "../components/UserTable";
import UserForm from "../components/UserForm";
import ProviderDocumentReviewList from "../components/documentUploads/ProviderDocumentReviewList";

const Clients = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const deepLinkedProviderIdRef = useRef(null);

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileClient, setProfileClient] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [accountConfirmOpen, setAccountConfirmOpen] = useState(false);
  const [accountPendingValue, setAccountPendingValue] = useState(null);
  const [providerServices, setProviderServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState({ open: false, url: "", title: "" });

  const getProviderAboutUsText = (providerData) => {
    if (!providerData) return "";
    return (
      providerData.providerIntroduction ||
      providerData.bio ||
      providerData.about ||
      providerData.description ||
      providerData.profile?.providerIntroduction ||
      providerData.profile?.bio ||
      providerData.profile?.about ||
      providerData.profile?.description ||
      providerData.provider_onboarding?.profile?.providerIntroduction ||
      providerData.provider_onboarding?.profile?.bio ||
      providerData.provider_onboarding?.profile?.about ||
      providerData.provider_onboarding?.profile?.description ||
      ""
    );
  };

  const approvalStatus =
    (profileClient && (profileClient.approvalStatus || profileClient.approval_status)) ||
    (profileClient && profileClient.provider_onboarding && (profileClient.provider_onboarding.approvalStatus || profileClient.provider_onboarding.approval_status)) ||
    null;
  const providerAboutUsText = getProviderAboutUsText(profileClient);

  const normalizeStatusValue = (value) =>
    String(value ?? "")
      .trim()
      .toLowerCase();

  const isProviderRoleDeactivated = (provider = {}) => {
    const safeProvider = provider || {};
    const accountStatus = normalizeStatusValue(
      safeProvider.accountStatus || safeProvider.status,
    );
    const roleStatus = normalizeStatusValue(
      safeProvider?.roleDeactivation?.client?.status ||
        safeProvider?.roleDeactivation?.provider?.status,
    );

    return (
      safeProvider.accountActive === false ||
      safeProvider.inactive === true ||
      accountStatus === "deactivated" ||
      roleStatus === "deactivated" ||
      safeProvider.providerAccountDisabled === true
    );
  };

  const providerAccountDeactivated = isProviderRoleDeactivated(profileClient);

  // Fetch clients from Firestore
  const fetchClients = async () => {
    try {
      setLoading(true);
      setError("");

      const q = query(
        collection(db, "users"),
        where("roles.client", "==", true)
      );

      const querySnapshot = await getDocs(q);
      const clientsData = [];

      querySnapshot.forEach((doc) => {
        clientsData.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setClients(clientsData);
    } catch (err) {
      console.error("Error fetching clients:", err);
      setError("Failed to fetch clients. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    const providerIdToOpen = location.state?.openProviderId;
    if (!providerIdToOpen || clients.length === 0) return;

    if (deepLinkedProviderIdRef.current === providerIdToOpen) {
      return;
    }

    const matchedProvider = clients.find((client) => client.id === providerIdToOpen);
    if (!matchedProvider) {
      navigate(location.pathname, { replace: true, state: null });
      return;
    }

    deepLinkedProviderIdRef.current = providerIdToOpen;
    handleViewProfile(matchedProvider);
    navigate(location.pathname, { replace: true, state: null });
  }, [clients, location.state, location.pathname, navigate]);

  // Helper to format different timestamp shapes (Firestore Timestamp, {seconds,nanoseconds}, ISO string)
  const formatTimestamp = (dateField) => {
    if (!dateField) return "—";
    // Firestore Timestamp
    if (dateField.toDate && typeof dateField.toDate === "function") {
      try {
        return dateField.toDate().toLocaleString();
      } catch {
        return String(dateField);
      }
    }
    // Map with seconds/nanoseconds
    if (
      dateField.seconds !== undefined &&
      dateField.nanoseconds !== undefined
    ) {
      try {
        return new Date(
          dateField.seconds * 1000 + Math.floor(dateField.nanoseconds / 1000000)
        ).toLocaleString();
      } catch {
        return String(dateField);
      }
    }
    // ISO string
    if (typeof dateField === "string") {
      try {
        const d = new Date(dateField);
        if (!isNaN(d)) return d.toLocaleString();
      } catch (e) {}
      return dateField;
    }
    // RN-style map object { day, month, year }
    if (
      typeof dateField === "object" &&
      dateField !== null &&
      dateField.day !== undefined &&
      dateField.month !== undefined &&
      (dateField.year !== undefined || dateField.Y !== undefined)
    ) {
      const day = String(dateField.day).padStart(2, "0");
      const month = String(dateField.month).padStart(2, "0");
      const year = String(dateField.year || dateField.Y);
      return `${day}/${month}/${year}`;
    }
    return String(dateField);
  };

  const toEpochMillis = (dateField) => {
    if (!dateField) return 0;

    if (dateField.toDate && typeof dateField.toDate === "function") {
      try {
        return dateField.toDate().getTime();
      } catch {
        return 0;
      }
    }

    if (
      typeof dateField === "object" &&
      dateField.seconds !== undefined &&
      dateField.nanoseconds !== undefined
    ) {
      return dateField.seconds * 1000 + Math.floor(dateField.nanoseconds / 1000000);
    }

    if (dateField instanceof Date) {
      return dateField.getTime();
    }

    if (typeof dateField === "number") {
      return Number.isFinite(dateField) ? dateField : 0;
    }

    if (typeof dateField === "string") {
      const parsed = new Date(dateField);
      return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
    }

    return 0;
  };

  const getFirstUrlFromValue = (value) => {
    if (!value) return "";
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed || "";
    }
    if (typeof value === "object") {
      return (
        getFirstUrlFromValue(value.url) ||
        getFirstUrlFromValue(value.uri) ||
        getFirstUrlFromValue(value.src) ||
        getFirstUrlFromValue(value.image) ||
        getFirstUrlFromValue(value.imageUrl) ||
        getFirstUrlFromValue(value.downloadURL)
      );
    }
    return "";
  };

  const resolveServiceImageUrl = (serviceData) => {
    if (!serviceData || typeof serviceData !== "object") return "";

    const directCandidates = [
      serviceData.image,
      serviceData.imageUrl,
      serviceData.serviceImage,
      serviceData.thumbnail,
      serviceData.thumbnailUrl,
      serviceData.photoURL,
      serviceData.photoUrl,
      serviceData.coverImage,
      serviceData.coverImageUrl,
      serviceData.bannerImage,
      serviceData.media?.image,
      serviceData.media?.imageUrl,
    ];

    for (const candidate of directCandidates) {
      const found = getFirstUrlFromValue(candidate);
      if (found) return found;
    }

    const arrayCandidates = [serviceData.images, serviceData.photos, serviceData.gallery];
    for (const arr of arrayCandidates) {
      if (!Array.isArray(arr)) continue;
      for (const entry of arr) {
        const found = getFirstUrlFromValue(entry);
        if (found) return found;
      }
    }

    return "";
  };

  const parseNumericValue = (value) => {
    if (value === undefined || value === null || value === "") return null;
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : null;
    }
    const text = String(value).trim();
    if (!text) return null;
    const cleaned = text.replace(/[^0-9.-]/g, "");
    if (!cleaned) return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const resolveServicePriceValue = (serviceData) => {
    if (!serviceData || typeof serviceData !== "object") return null;

    const candidates = [
      serviceData.price,
      serviceData.amount,
      serviceData.basePrice,
      serviceData.servicePrice,
      serviceData.startingPrice,
      serviceData.rate,
      serviceData.total,
      serviceData.pricing?.price,
      serviceData.pricing?.amount,
    ];

    for (const candidate of candidates) {
      if (candidate === 0) return 0;
      if (candidate === undefined || candidate === null || candidate === "") continue;

      const parsed = parseNumericValue(candidate);
      if (parsed !== null) return parsed;

      if (typeof candidate === "string") {
        const text = candidate.trim();
        if (text) return text;
      }
    }

    return null;
  };

  const resolveServiceDurationValue = (serviceData) => {
    if (!serviceData || typeof serviceData !== "object") return null;

    const candidates = [
      serviceData.duration,
      serviceData.durationMinutes,
      serviceData.estimatedDuration,
      serviceData.serviceDuration,
      serviceData.time,
      serviceData.length,
      serviceData.durationLabel,
      serviceData.durationText,
      serviceData.pricing?.duration,
      serviceData.pricing?.durationMinutes,
    ];

    for (const candidate of candidates) {
      if (candidate === undefined || candidate === null || candidate === "") continue;

      if (typeof candidate === "number") {
        if (Number.isFinite(candidate)) return candidate;
        continue;
      }

      if (typeof candidate === "string") {
        const text = candidate.trim();
        if (!text) continue;
        const parsed = parseNumericValue(text);
        if (parsed !== null && /^\d+(\.\d+)?$/.test(text)) return parsed;
        return text;
      }

      if (typeof candidate === "object") {
        const nestedValue =
          candidate.value ??
          candidate.minutes ??
          candidate.duration ??
          candidate.time ??
          null;
        const nestedUnit = candidate.unit || candidate.label || candidate.type || "";
        if (nestedValue === undefined || nestedValue === null || nestedValue === "") {
          continue;
        }
        if (nestedUnit) {
          return `${nestedValue} ${nestedUnit}`;
        }
        const parsed = parseNumericValue(nestedValue);
        if (parsed !== null) return parsed;
        return String(nestedValue);
      }
    }

    return null;
  };

  const formatServicePrice = (priceValue) => {
    if (priceValue === undefined || priceValue === null || priceValue === "") return "-";

    if (typeof priceValue === "number" && Number.isFinite(priceValue)) {
      return `$${priceValue.toFixed(2)}`;
    }

    const text = String(priceValue).trim();
    if (!text) return "-";

    if (/[a-zA-Z]/.test(text)) return text;

    const parsed = parseNumericValue(text);
    if (parsed !== null) {
      return `$${parsed.toFixed(2)}`;
    }

    if (text.startsWith("$")) return text;
    return `$${text}`;
  };

  const formatServiceDuration = (durationValue) => {
    if (durationValue === undefined || durationValue === null || durationValue === "") {
      return "-";
    }

    if (typeof durationValue === "number" && Number.isFinite(durationValue)) {
      return `${durationValue} mins`;
    }

    const text = String(durationValue).trim();
    if (!text) return "-";

    if (/^\d+(\.\d+)?$/.test(text)) {
      return `${text} mins`;
    }

    return text;
  };

  const normalizeProviderService = (serviceData, fallbackIndex = 0) => {
    if (!serviceData || typeof serviceData !== "object") return null;

    const title = String(
      serviceData.title ||
        serviceData.name ||
        serviceData.serviceName ||
        serviceData.service ||
        serviceData.label ||
        serviceData.category ||
        "Service"
    ).trim();

    const statusText = String(serviceData.status || "").toLowerCase();
    const active =
      serviceData.active !== undefined
        ? Boolean(serviceData.active)
        : !["inactive", "disabled", "archived", "deleted"].includes(statusText);

    const fallbackSlug = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || `service-${fallbackIndex}`;

    return {
      ...serviceData,
      id:
        serviceData.id ||
        serviceData.serviceId ||
        serviceData.uid ||
        `normalized-${fallbackIndex}-${fallbackSlug}`,
      title,
      name: title,
      description:
        serviceData.description ||
        serviceData.subtitle ||
        serviceData.details ||
        serviceData.summary ||
        "",
      image: resolveServiceImageUrl(serviceData),
      price: resolveServicePriceValue(serviceData),
      duration: resolveServiceDurationValue(serviceData),
      category:
        serviceData.category ||
        serviceData.serviceCategory ||
        serviceData.type ||
        "",
      active,
    };
  };

  const fetchProviderServicesForClient = async (providerId) => {
    if (!providerId) return [];

    const providerIdText = String(providerId);
    const hasProviderIdMatch = (value) =>
      value !== undefined && value !== null && String(value) === providerIdText;

    const providersArrayContainsProvider = (providersValue) => {
      if (!Array.isArray(providersValue)) return false;
      return providersValue.some((providerEntry) => {
        if (providerEntry === undefined || providerEntry === null) return false;
        if (
          typeof providerEntry === "string" ||
          typeof providerEntry === "number"
        ) {
          return hasProviderIdMatch(providerEntry);
        }
        if (typeof providerEntry === "object") {
          return [
            providerEntry.id,
            providerEntry.providerId,
            providerEntry.provider_id,
            providerEntry.uid,
            providerEntry.userId,
            providerEntry.ownerId,
            providerEntry.clientId,
          ].some(hasProviderIdMatch);
        }
        return false;
      });
    };

    const serviceBelongsToProvider = (serviceData) => {
      if (!serviceData || typeof serviceData !== "object") return false;

      if (providersArrayContainsProvider(serviceData.providers)) return true;

      return [
        serviceData.ownerId,
        serviceData.providerId,
        serviceData.provider_id,
        serviceData.clientId,
        serviceData.userId,
        serviceData.createdBy,
        serviceData.owner?.id,
        serviceData.owner?.uid,
        serviceData.owner?.providerId,
        serviceData.provider?.id,
        serviceData.provider?.uid,
        serviceData.provider?.providerId,
        serviceData.provider?.userId,
        serviceData.client?.id,
        serviceData.client?.uid,
      ].some(hasProviderIdMatch);
    };

    const servicesCol = collection(db, "services");
    const providerServiceQueries = [
      query(servicesCol, where("ownerId", "==", providerId)),
      query(servicesCol, where("providerId", "==", providerId)),
      query(servicesCol, where("provider_id", "==", providerId)),
      query(servicesCol, where("clientId", "==", providerId)),
      query(servicesCol, where("providers", "array-contains", providerId)),
    ];

    const servicesMap = new Map();
    for (const servicesQuery of providerServiceQueries) {
      try {
        const servicesSnapshot = await getDocs(servicesQuery);
        servicesSnapshot.forEach((serviceDoc) => {
          if (!servicesMap.has(serviceDoc.id)) {
            servicesMap.set(serviceDoc.id, {
              id: serviceDoc.id,
              ...serviceDoc.data(),
            });
          }
        });
      } catch (serviceErr) {
        console.warn(
          "Could not fetch provider services using one of the fallback query fields",
          serviceErr
        );
      }
    }

    // Backward-compatible fallback for mixed provider reference shapes
    // (for example providers array of objects or nested owner/provider maps).
    if (servicesMap.size === 0) {
      try {
        const allServicesSnapshot = await getDocs(servicesCol);
        allServicesSnapshot.forEach((serviceDoc) => {
          const serviceData = serviceDoc.data();
          if (!serviceBelongsToProvider(serviceData)) return;
          if (!servicesMap.has(serviceDoc.id)) {
            servicesMap.set(serviceDoc.id, {
              id: serviceDoc.id,
              ...serviceData,
            });
          }
        });
      } catch (fallbackErr) {
        console.warn("Could not run fallback service scan for provider", fallbackErr);
      }
    }

    return Array.from(servicesMap.values())
      .map((service, index) => normalizeProviderService(service, index))
      .filter(Boolean)
      .sort((a, b) => {
        const aTime = toEpochMillis(a.createdAt || a.updatedAt || a.lastUpdatedAt);
        const bTime = toEpochMillis(b.createdAt || b.updatedAt || b.lastUpdatedAt);
        return bTime - aTime;
      });
  };

  const normalizeServiceEntryList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .map((entry) => {
          if (!entry) return null;
          if (typeof entry === "string" || typeof entry === "number") {
            const text = String(entry).trim();
            return text ? { title: text, name: text } : null;
          }
          if (typeof entry === "object") {
            const text = String(
              entry.name ||
                entry.title ||
                entry.label ||
                entry.serviceName ||
                entry.service ||
                ""
            ).trim();
            if (!text) return null;
            return {
              ...entry,
              title: text,
              name: text,
              description:
                entry.description ||
                entry.subtitle ||
                entry.details ||
                entry.summary ||
                "",
              price:
                entry.price ??
                entry.amount ??
                entry.basePrice ??
                entry.servicePrice ??
                entry.startingPrice ??
                null,
              duration:
                entry.duration ??
                entry.durationMinutes ??
                entry.estimatedDuration ??
                entry.serviceDuration ??
                entry.time ??
                null,
              image:
                entry.image ||
                entry.imageUrl ||
                entry.serviceImage ||
                entry.thumbnail ||
                entry.photoURL ||
                "",
              category:
                entry.category ||
                entry.serviceCategory ||
                entry.type ||
                "",
            };
          }
          const text = String(entry).trim();
          return text ? { title: text, name: text } : null;
        })
        .filter(Boolean);
    }

    return String(value)
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean)
      .map((entry) => ({ title: entry, name: entry }));
  };

  const deriveProviderServicesFromProfile = (providerData) => {
    if (!providerData) return [];

    const serviceEntries = [
      ...normalizeServiceEntryList(providerData.servicesOffered),
      ...normalizeServiceEntryList(providerData.profile?.servicesOffered),
      ...normalizeServiceEntryList(providerData.provider_onboarding?.servicesOffered),
      ...normalizeServiceEntryList(providerData.provider_onboarding?.profile?.servicesOffered),
      ...normalizeServiceEntryList(providerData.serviceCategories),
      ...normalizeServiceEntryList(providerData.serviceTypes),
      ...normalizeServiceEntryList(providerData.skills),
      ...normalizeServiceEntryList(providerData.categories),
    ];

    const mergedByName = new Map();
    serviceEntries.forEach((entry, index) => {
      const normalizedEntry = normalizeProviderService(
        {
          ...entry,
          fromProfile: true,
          active: entry.active !== undefined ? entry.active : true,
        },
        index
      );

      if (!normalizedEntry) return;

      const key = String(normalizedEntry.title || normalizedEntry.name || "")
        .trim()
        .toLowerCase();
      if (!key) return;

      if (!mergedByName.has(key)) {
        mergedByName.set(key, normalizedEntry);
        return;
      }

      const existing = mergedByName.get(key);
      mergedByName.set(key, {
        ...existing,
        description: existing.description || normalizedEntry.description,
        image: existing.image || normalizedEntry.image,
        price: existing.price ?? normalizedEntry.price,
        duration: existing.duration || normalizedEntry.duration,
        category: existing.category || normalizedEntry.category,
      });
    });

    return Array.from(mergedByName.values()).map((service, index) => ({
      ...service,
      id:
        service.id ||
        `profile-service-${index}-${String(service.title || service.name || "service")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")}`,
      fromProfile: true,
    }));
  };

  const normalizeDocumentMap = (documentsValue) => {
    if (!documentsValue || typeof documentsValue !== "object") return {};

    const normalizeDocumentValue = (value) => {
      if (!value) return "";
      if (typeof value === "string") return value.trim();

      if (Array.isArray(value)) {
        for (const item of value) {
          const normalized = normalizeDocumentValue(item);
          if (normalized) return normalized;
        }
        return "";
      }

      if (typeof value === "object") {
        return (
          normalizeDocumentValue(value.url) ||
          normalizeDocumentValue(value.downloadURL) ||
          normalizeDocumentValue(value.fileUrl) ||
          normalizeDocumentValue(value.uri) ||
          normalizeDocumentValue(value.path) ||
          ""
        );
      }

      return "";
    };

    const normalized = {};
    Object.entries(documentsValue).forEach(([key, value]) => {
      const normalizedValue = normalizeDocumentValue(value);
      if (!normalizedValue) return;
      normalized[key] = normalizedValue;
    });

    return normalized;
  };

  // Handle create client
  const handleCreate = () => {
    setEditingClient(null);
    setFormOpen(true);
  };

  // Handle edit client
  const handleEdit = async (client) => {
    if (!client || !client.id) return;

    try {
      const detailsSnap = await getDocs(collection(db, "users", client.id, "details"));
      const detailsMap = {};
      detailsSnap.forEach((detailsDoc) => {
        detailsMap[detailsDoc.id] = detailsDoc.data();
      });

      const merged = { ...client };
      if (detailsMap.bankingDetails) merged.bankingDetails = detailsMap.bankingDetails;
      if (detailsMap.businessInformation)
        merged.businessInformation = detailsMap.businessInformation;
      if (detailsMap.profile) merged.profile = { ...(merged.profile || {}), ...detailsMap.profile };
      if (detailsMap.provider_onboarding) {
        merged.provider_onboarding = {
          ...(merged.provider_onboarding || {}),
          ...detailsMap.provider_onboarding,
        };
      }

      const mergedDocuments = normalizeDocumentMap({
        ...(client.documents && typeof client.documents === "object" ? client.documents : {}),
        ...(detailsMap.documents && typeof detailsMap.documents === "object" ? detailsMap.documents : {}),
        ...(detailsMap.provider_onboarding?.documents &&
        typeof detailsMap.provider_onboarding.documents === "object"
          ? detailsMap.provider_onboarding.documents
          : {}),
      });

      if (Object.keys(mergedDocuments).length > 0) {
        merged.documents = mergedDocuments;
      }

      setEditingClient(merged);
    } catch (editLoadErr) {
      console.warn("Could not preload provider details for edit dialog", editLoadErr);
      setEditingClient(client);
    }

    setFormOpen(true);
  };

  // Handle delete client
  const handleDelete = (client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  // Open provider profile (detailed UI) and merge any `details` subcollection docs
  const handleViewProfile = async (client) => {
    if (!client || !client.id) return;
    setProfileDialogOpen(true);
    setProfileClient(null);
    setProfileLoading(true);
    setProviderServices([]);
    setServicesLoading(true);
    try {
      // Fetch any docs under users/{uid}/details
      const detailsCol = collection(db, "users", client.id, "details");
      const [detailsSnap, loadedProviderServices] = await Promise.all([
        getDocs(detailsCol),
        fetchProviderServicesForClient(client.id),
      ]);
      const detailsMap = {};
      const nestedCandidates = [
        "documents",
        "files",
        "attachments",
        "onboarding_documents",
        "profile",
        "photos",
      ];
      for (const d of detailsSnap.docs) {
        const data = d.data();
        // try to fetch nested subcollections for this details doc
        for (const subName of nestedCandidates) {
          try {
            const nestedRef = collection(
              db,
              "users",
              client.id,
              "details",
              d.id,
              subName
            );
            const nestedSnap = await getDocs(nestedRef);
            if (!nestedSnap.empty) {
              // attach nested docs array to the details doc object
              data[subName] = nestedSnap.docs.map((nd) => ({
                id: nd.id,
                data: nd.data(),
              }));
            }
          } catch (err) {
            // ignore missing nested collections
          }
        }
        detailsMap[d.id] = data;
      }

      // Merge top-level client with details maps; prefer details where appropriate
      const merged = { ...client, details: detailsMap };

      // Copy each details doc to merged under its doc id so templates can access them regardless of naming
      for (const [docId, docData] of Object.entries(detailsMap)) {
        merged[docId] = docData;
      }

      // Helper to find a details doc by multiple possible id variants
      const findDetailsDoc = (candidates) => {
        for (const name of candidates) {
          if (detailsMap[name]) return detailsMap[name];
          if (merged[name]) return merged[name];
        }
        return null;
      };

      // Known candidate names for common detail docs
      const bankingCandidates = [
        "bankingDetails",
        "banking",
        "bank_details",
        "banking_details",
      ];
      const businessCandidates = [
        "businessInformation",
        "business_info",
        "businessInformation",
        "businessInformation",
      ];
      const profileCandidates = ["profile", "profileInfo", "profile_info"];
      const onboardingCandidates = [
        "provider_onboarding",
        "providerOnboarding",
        "onboarding",
        "onboarding_details",
        "onboardingDetails",
      ];

      const bankingDoc = findDetailsDoc(bankingCandidates);
      const businessDoc = findDetailsDoc(businessCandidates);
      const profileDoc = findDetailsDoc(profileCandidates);
      const onboardingDoc =
        findDetailsDoc(onboardingCandidates) ||
        detailsMap.provider_onboarding ||
        detailsMap.onboarding ||
        null;

      if (bankingDoc) merged.bankingDetails = bankingDoc;
      if (businessDoc) merged.businessInformation = businessDoc;
      if (profileDoc)
        merged.profile = { ...(merged.profile || {}), ...profileDoc };
      if (onboardingDoc) merged.provider_onboarding = onboardingDoc;

      // If provider_onboarding contains nested sections, copy them to merged for easier access
      if (merged.provider_onboarding) {
        if (merged.provider_onboarding.bankingDetails) {
          merged.bankingDetails =
            merged.bankingDetails || merged.provider_onboarding.bankingDetails;
        }
        if (merged.provider_onboarding.businessInformation) {
          merged.businessInformation =
            merged.businessInformation ||
            merged.provider_onboarding.businessInformation;
        }
        if (merged.provider_onboarding.profile) {
          merged.profile = {
            ...(merged.profile || {}),
            ...merged.provider_onboarding.profile,
          };
        }
        if (merged.provider_onboarding.documents) {
          merged.documents =
            merged.documents || merged.provider_onboarding.documents;
        }
      }

      // Copy common banking fields to root for convenience (check both doc shapes)
      if (merged.bankingDetails) {
        merged.bankName =
          merged.bankName ||
          merged.bankingDetails.bankName ||
          merged.bankingDetails.bank_name ||
          merged.bankingDetails.bank;
        merged.accountName =
          merged.accountName ||
          merged.bankingDetails.accountName ||
          merged.bankingDetails.account_name ||
          merged.bankingDetails.account;
        merged.bsb =
          merged.bsb ||
          merged.bankingDetails.bsb ||
          merged.bankingDetails.bsbNumber;
        merged.accountNumber =
          merged.accountNumber ||
          merged.bankingDetails.accountNumber ||
          merged.bankingDetails.account_number;
      }

      // Business info
      if (merged.businessInformation) {
        merged.abn =
          merged.abn ||
          merged.businessInformation.abnNumber ||
          merged.businessInformation.abn_number ||
          merged.businessInformation.abn;
        merged.tfn =
          merged.tfn ||
          merged.businessInformation.tfnNumber ||
          merged.businessInformation.tfn_number ||
          merged.businessInformation.tfn;
        merged.businessName =
          merged.businessName ||
          merged.businessInformation.businessName ||
          merged.businessInformation.business_name;
      }

      // Copy visa fields from provider_onboarding
      if (merged.provider_onboarding) {
        merged.visaCategory =
          merged.visaCategory ||
          merged.provider_onboarding.visaCategory ||
          merged.provider_onboarding.visa_category ||
          merged.provider_onboarding.visaType;
        merged.visaExpiry =
          merged.visaExpiry ||
          merged.provider_onboarding.visaExpiry ||
          merged.provider_onboarding.visa_expiry;
        merged.nationalityStatus =
          merged.nationalityStatus ||
          merged.provider_onboarding.nationalityStatus ||
          merged.provider_onboarding.nationality_status;
        if (
          merged.provider_onboarding.servicesOffered &&
          (!merged.servicesOffered || merged.servicesOffered.length === 0)
        ) {
          merged.servicesOffered = Array.isArray(
            merged.provider_onboarding.servicesOffered
          )
            ? merged.provider_onboarding.servicesOffered
            : String(merged.provider_onboarding.servicesOffered)
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
        }
      }

      // Copy visa fields from profile (if not already set)
      if (merged.profile) {
        merged.nationalityStatus =
          merged.nationalityStatus ||
          merged.profile.nationalityStatus ||
          merged.profile.nationality_status;
        merged.visaCategory =
          merged.visaCategory ||
          merged.profile.visaCategory ||
          merged.profile.visa_category;
        merged.visaExpiry =
          merged.visaExpiry ||
          merged.profile.visaExpiry ||
          merged.profile.visa_expiry;
      }

      // Copy some commonly used nested fields to root for easier templating
      if (merged.bankingDetails) {
        merged.bankName = merged.bankingDetails.bankName || merged.bankName;
        merged.accountName =
          merged.bankingDetails.accountName || merged.accountName;
        merged.bsb = merged.bankingDetails.bsb || merged.bsb;
        merged.accountNumber =
          merged.bankingDetails.accountNumber || merged.accountNumber;
      }
      if (merged.businessInformation) {
        merged.abn = merged.businessInformation.abnNumber || merged.abn;
        merged.tfn = merged.businessInformation.tfnNumber || merged.tfn;
      }
      if (merged.profile && (!merged.servicesOffered || merged.servicesOffered.length === 0)) {
        merged.servicesOffered =
          merged.profile.servicesOffered || merged.servicesOffered;
      }
      if (merged.profile) {
        merged.preferredGender =
          merged.profile.preferredGender || merged.preferredGender;
      }

      if (!merged.providerIntroduction) {
        const mergedAboutUsText = getProviderAboutUsText(merged);
        if (mergedAboutUsText) {
          merged.providerIntroduction = mergedAboutUsText;
        }
      }

      const profileDerivedServices = deriveProviderServicesFromProfile(merged);

      setProfileClient(merged);
      setProviderServices(
        loadedProviderServices.length > 0 ? loadedProviderServices : profileDerivedServices
      );
    } catch (err) {
      console.error("Failed to load profile details for user", client.id, err);
      // fallback to client only
      setProfileClient(client);
      setProviderServices(deriveProviderServicesFromProfile(client));
    } finally {
      setProfileLoading(false);
      setServicesLoading(false);
    }
  };

  // Confirm delete with cascade deletion
  const confirmDelete = async () => {
    if (!clientToDelete) return;

    try {
      setFormLoading(true);
      const clientId = clientToDelete.id;

      // Delete all services owned by this client
      const servicesQuery = query(
        collection(db, "services"),
        where("ownerId", "==", clientId)
      );
      const servicesSnapshot = await getDocs(servicesQuery);
      const serviceDeletePromises = servicesSnapshot.docs.map((serviceDoc) =>
        deleteDoc(doc(db, "services", serviceDoc.id))
      );
      await Promise.all(serviceDeletePromises);

      // Delete all bookings where client is the provider
      const bookingsQuery = query(
        collection(db, "bookings"),
        where("clientId", "==", clientId)
      );
      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookingDeletePromises = bookingsSnapshot.docs.map((bookingDoc) =>
        deleteDoc(doc(db, "bookings", bookingDoc.id))
      );
      await Promise.all(bookingDeletePromises);

      // Delete all earnings/transactions related to this client
      const earningsQuery = query(
        collection(db, "earnings"),
        where("providerId", "==", clientId)
      );
      const earningsSnapshot = await getDocs(earningsQuery);
      const earningDeletePromises = earningsSnapshot.docs.map((earningDoc) =>
        deleteDoc(doc(db, "earnings", earningDoc.id))
      );
      await Promise.all(earningDeletePromises);

      // Mark the user document for deletion and disable account
      await updateDoc(doc(db, "users", clientId), {
        deleted: true,
        deletedAt: new Date().toISOString(),
        disabled: true,
        email: `deleted_${clientId}@deleted.com`, // Change email to prevent login
      });

      // Delete the client user document from Firestore
      // This will automatically trigger the Cloud Function to delete the auth account
      await deleteDoc(doc(db, "users", clientId));

      setClients((prev) => prev.filter((client) => client.id !== clientId));

      showSnackbar(
        `Client deleted successfully! Removed ${servicesSnapshot.size} services, ${bookingsSnapshot.size} bookings, and ${earningsSnapshot.size} earnings records. Authentication account will be automatically deleted.`,
        "success"
      );
    } catch (err) {
      console.error("Error deleting client and related data:", err);
      showSnackbar("Failed to delete client. Please try again.", "error");
    } finally {
      setFormLoading(false);
      setDeleteDialogOpen(false);
      setClientToDelete(null);
    }
  };

  // Handle form submission
  const handleFormSubmit = async (formData) => {
    try {
      setFormLoading(true);

      const normalizedDocuments = normalizeDocumentMap(
        formData.documents ||
          editingClient?.documents ||
          editingClient?.provider_onboarding?.documents ||
          {}
      );

      // Prepare data for Firestore (use ISO date strings and consistent field names to match React Native)
      const dobValue = formData.dob
        ? formData.dob instanceof Date
          ? formData.dob.toISOString().split("T")[0]
          : String(formData.dob)
        : null;

      const existingRoles = editingClient ? editingClient.roles || {} : {};

      const userData = {
        name: (
          formData.name ||
          `${formData.firstName || ""} ${formData.lastName || ""}`
        ).trim(),
        email: formData.email
          ? String(formData.email).trim().toLowerCase()
          : editingClient?.email || "",
        phone:
          formData.phone || formData.phoneNumber
            ? String(formData.phone || formData.phoneNumber).replace(/\s/g, "")
            : editingClient?.phone || "",
        dob: dobValue,
        gender: formData.gender || editingClient?.gender || "",
        address:
          typeof formData.address === "string"
            ? formData.address
            : formData.formattedAddress || "",
        location:
          formData.location &&
          (formData.location.latitude || formData.location.longitude)
            ? {
                latitude: String(formData.location.latitude),
                longitude: String(formData.location.longitude),
              }
            : editingClient?.location || undefined,
        formattedAddress:
          formData.formattedAddress ||
          editingClient?.formattedAddress ||
          undefined,
        address_place_id:
          formData.address_place_id ||
          editingClient?.address_place_id ||
          undefined,
        activeRole: "client",
        roles: { ...existingRoles, client: true },
        approvalStatus: editingClient
          ? editingClient.approvalStatus || "approved"
          : "pending",
        photoURL: formData.photoURL || editingClient?.photoURL || "",
        totalEarnings: editingClient?.totalEarnings ?? 0,
        pendingEarnings: editingClient?.pendingEarnings ?? 0,
        receivedEarnings: editingClient?.receivedEarnings ?? 0,
        createdAt: editingClient?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),

        // Business / provider fields
        businessName:
          formData.businessName || editingClient?.businessName || "",
        businessType:
          formData.businessType || editingClient?.businessType || "",
        abn: formData.abn || editingClient?.abn || "",

        // Additional profile fields (kept if provided)
        nationality: formData.nationality || editingClient?.nationality || "",
        experience: formData.experience || editingClient?.experience || "",
        preferredGender:
          formData.preferredGender || editingClient?.preferredGender || "",
        servicesOffered: Array.isArray(formData.servicesOffered)
          ? formData.servicesOffered
          : editingClient?.servicesOffered || [],
        // Visa / nationality fields
        visaCategory:
          formData.visaCategory || editingClient?.visaCategory || undefined,
        visaExpiry: formData.visaExpiry
          ? formData.visaExpiry instanceof Date
            ? formData.visaExpiry.toISOString().split("T")[0]
            : String(formData.visaExpiry)
          : editingClient?.visaExpiry || undefined,
        visaExpiryTimestamp: formData.visaExpiry
          ? formData.visaExpiry instanceof Date
            ? Timestamp.fromDate(formData.visaExpiry)
            : editingClient?.visaExpiryTimestamp || null
          : editingClient?.visaExpiryTimestamp || null,
        nationalityStatus:
          formData.nationalityStatus ||
          editingClient?.nationalityStatus ||
          undefined,
        approvedAt:
          formData.approvedAt || editingClient?.approvedAt || undefined,
        rejectedAt:
          formData.rejectedAt || editingClient?.rejectedAt || undefined,
        documents: normalizedDocuments,
      };

      let docRef = null;
      if (editingClient) {
        // Update existing client
        await updateDoc(doc(db, "users", editingClient.id), userData);

        // Update local state
        setClients((prev) =>
          prev.map((client) =>
            client.id === editingClient.id ? { ...client, ...userData } : client
          )
        );

        showSnackbar("Client updated successfully", "success");
      } else {
        // Create new client
        userData.createdAt = new Date().toISOString();
        docRef = await addDoc(collection(db, "users"), userData);

        // Add to local state
        setClients((prev) => [...prev, { id: docRef.id, ...userData }]);

        showSnackbar(
          "✅ Client application submitted! Your application will be reviewed within 24 hours.",
          "success"
        );
      }

      // Persist details subcollection documents (banking, business, provider_onboarding)
      try {
        const uid = editingClient
          ? editingClient.id
          : docRef
          ? docRef.id
          : null;
        if (uid) {
          // bankingDetails
          const banking = {
            bankName: formData.bankName || undefined,
            accountName: formData.accountName || undefined,
            bsb: formData.bsb || undefined,
            accountNumber: formData.accountNumber || undefined,
          };
          await setDoc(
            doc(db, "users", uid, "details", "bankingDetails"),
            banking,
            { merge: true }
          );

          // businessInformation
          const businessInfo = {
            abnNumber: formData.abnNumber || formData.abn || undefined,
            tfnNumber: formData.tfnNumber || undefined,
            businessName: formData.businessName || undefined,
            businessType: formData.businessType || undefined,
          };
          await setDoc(
            doc(db, "users", uid, "details", "businessInformation"),
            businessInfo,
            { merge: true }
          );

          // provider_onboarding
          const providerOnboarding = {};
          if (formData.visaCategory)
            providerOnboarding.visaCategory = formData.visaCategory;
          if (formData.visaExpiry) {
            // store as map { day, month, year } to match RN
            const d =
              formData.visaExpiry instanceof Date
                ? formData.visaExpiry
                : new Date(formData.visaExpiry);
            providerOnboarding.visaExpiry = {
              day: String(d.getUTCDate()).padStart(2, "0"),
              month: String(d.getUTCMonth() + 1).padStart(2, "0"),
              year: String(d.getUTCFullYear()),
            };
            providerOnboarding.submittedAt = editingClient?.provider_onboarding
              ?.submittedAt
              ? editingClient.provider_onboarding.submittedAt
              : formData.visaExpiry
              ? new Date().toISOString()
              : undefined;
          }
          if (
            formData.servicesOffered &&
            Array.isArray(formData.servicesOffered)
          )
            providerOnboarding.servicesOffered = formData.servicesOffered;
          if (Object.keys(normalizedDocuments).length > 0) {
            providerOnboarding.documents = normalizedDocuments;
          }
          providerOnboarding.updatedAt = Timestamp.fromDate(new Date());
          await setDoc(
            doc(db, "users", uid, "details", "provider_onboarding"),
            providerOnboarding,
            { merge: true }
          );

          if (Object.keys(normalizedDocuments).length > 0) {
            await setDoc(
              doc(db, "users", uid, "details", "documents"),
              normalizedDocuments,
              { merge: true }
            );
          }
        }
      } catch (err) {
        console.error("Failed to write details subdocs", err);
      }

      setFormOpen(false);
      setEditingClient(null);
    } catch (err) {
      console.error("Error saving client:", err);
      showSnackbar("Failed to save client", "error");
    } finally {
      setFormLoading(false);
    }
  };

  // Handle account status toggle (activate/deactivate provider)
  const handleAccountStatusToggle = async (checked) => {
    if (!profileClient?.id) return;

    try {
      const isActive = checked;
      const userRef = doc(db, "users", profileClient.id);
      const nowIso = new Date().toISOString();
      const roleDeactivation =
        profileClient?.roleDeactivation &&
        typeof profileClient.roleDeactivation === "object"
          ? profileClient.roleDeactivation
          : {};
      const existingRoleRecord = roleDeactivation.client || {};
      const nextRoleRecord = isActive
        ? {
            ...existingRoleRecord,
            status: "active",
            reactivatedAt: nowIso,
            reactivatedBy: "admin",
            updatedAt: nowIso,
          }
        : {
            ...existingRoleRecord,
            status: "deactivated",
            deactivatedAt: nowIso,
            deactivatedBy: "admin",
            reason: "Provider role deactivated by admin",
            updatedAt: nowIso,
          };

      if (isActive) {
        delete nextRoleRecord.reason;
      }

      const nextRoleDeactivation = {
        ...roleDeactivation,
        client: nextRoleRecord,
      };
      const nextPayload = {
        roleDeactivation: nextRoleDeactivation,
        providerAccountDisabled: !isActive,
        updatedAt: nowIso,
      };

      if (!isActive) {
        nextPayload.disabledReason = "Provider role deactivated by admin";
      } else if (
        String(profileClient?.disabledReason || "")
          .toLowerCase()
          .includes("provider role deactivated by admin")
      ) {
        nextPayload.disabledReason = "";
      }

      await updateDoc(userRef, nextPayload);

      // Update local state
      setProfileClient((prev) => ({
        ...prev,
        ...nextPayload,
      }));

      // Update in clients list
      setClients((prev) =>
        prev.map((client) =>
          client.id === profileClient.id
            ? { 
                ...client, 
                ...nextPayload,
              }
            : client
        )
      );

      showSnackbar(
        `Provider role ${isActive ? "activated" : "deactivated"} successfully`,
        "success"
      );
    } catch (err) {
      console.error("Error updating account status:", err);
      showSnackbar("Failed to update account status", "error");
    }
  };

  // Called when admin toggles the switch in the UI - show confirmation first
  const handleAccountToggleRequest = (checked) => {
    setAccountPendingValue(checked);
    setAccountConfirmOpen(true);
  };

  const handleConfirmAccountToggle = async () => {
    setAccountConfirmOpen(false);
    if (accountPendingValue === null) return;
    await handleAccountStatusToggle(accountPendingValue);
    setAccountPendingValue(null);
  };

  // Show snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  // Open image preview
  const handleImageClick = (url, title) => {
    setImagePreview({ open: true, url, title });
  };

  // Close image preview
  const handleCloseImagePreview = () => {
    setImagePreview({ open: false, url: "", title: "" });
  };

  return (
    <Box>
      {/* <Typography variant="h4" gutterBottom>
        Service Provider Management
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage all Service Provider in your system. You can create, edit, and delete client records with their business information.
      </Typography> */}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <UserTable
        users={clients}
        // title="Service Provider"
        onEdit={handleEdit}
        onDelete={handleDelete}
        onCreate={handleCreate}
        onView={handleViewProfile}
        loading={loading}
      />

      {/* Profile Dialog (detailed provider UI similar to screenshots) */}
      <Dialog
        open={profileDialogOpen}
        onClose={() => {
          setProfileDialogOpen(false);
          setProfileClient(null);
          setProviderServices([]);
          setServicesLoading(false);
        }}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            maxWidth: 1500,
            width: "calc(100% - 32px)",
            overflowX: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => {
                setProfileDialogOpen(false);
                setProfileClient(null);
                setProviderServices([]);
                setServicesLoading(false);
              }}
            >
              Back to Dashboard
            </Button>
          </Box>
        </DialogTitle>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{ 
                width: 64, 
                height: 64,
                cursor: profileClient?.photoURL ? 'pointer' : 'default',
                '&:hover': profileClient?.photoURL ? {
                  opacity: 0.8,
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s',
                } : {}
              }}
              src={profileClient?.photoURL}
              onClick={() => {
                if (profileClient?.photoURL) {
                  handleImageClick(profileClient.photoURL, `${profileClient?.name || 'Provider'} - Profile Photo`);
                }
              }}
            >
              <PersonIcon />
            </Avatar>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {profileClient?.name || "Unknown"}
              </Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
                  {profileClient?.onboardingDocumentsVerified && (
                    <Chip label="Verified" size="small" color="success" />
                  )}
                  {providerAccountDeactivated ? (
                    <Chip 
                      label="Account Deactivated" 
                      size="small" 
                      color="error" 
                      sx={{ fontWeight: 600 }}
                    />
                  ) : (
                    <Chip 
                      label="Account Active" 
                      size="small" 
                      color="success" 
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                  {profileClient?.activeRole && (
                    <Chip label='Provider' size="small" />
                  )}
                  {approvalStatus && (
                    <Chip
                      label={
                        typeof approvalStatus === "string"
                          ? approvalStatus.charAt(0).toUpperCase() + approvalStatus.slice(1)
                          : String(approvalStatus)
                      }
                      size="small"
                      color={
                        ("" + approvalStatus).toLowerCase() === "approved"
                          ? "success"
                          : ("" + approvalStatus).toLowerCase() === "pending"
                          ? "warning"
                          : ("" + approvalStatus).toLowerCase() === "rejected"
                          ? "error"
                          : "default"
                      }
                    />
                  )}
                </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Account Status:
              </Typography>
              <Switch
                checked={!providerAccountDeactivated}
                onChange={(e) => handleAccountToggleRequest(e.target.checked)}
                color={!providerAccountDeactivated ? "success" : "default"}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600,
                  color: !providerAccountDeactivated ? "success.main" : "error.main",
                }}
              >
                {!providerAccountDeactivated ? "Active" : "Deactivated"}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => {
                setEditingClient(profileClient);
                setFormOpen(true);
                setProfileDialogOpen(false);
                setProfileClient(null);
                setProviderServices([]);
                setServicesLoading(false);
              }}
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ overflowX: "hidden" }}>
          {profileLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : /* Header: avatar, name, badges, actions */
          null}
          {!profileLoading && (
            <>
              <Grid container spacing={2} alignItems="stretch" sx={{ mt: 3 }}>
                <Grid size={{ xs: 12, md: 6, lg: 4 }} sx={{ display: 'flex', minWidth: 0 }}>
                  <Paper sx={{ p: 2, borderRadius: 2,width:'100%', height: '100%', display: 'flex', flexDirection: 'column' }} variant="outlined">
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Box
                        sx={{
                          bgcolor: "#f5f5f7",
                          p: 1.25,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PersonIcon fontSize="small" />
                      </Box>
                      <Typography variant="h6">Personal Information</Typography>
                    </Box>
                    <Box sx={{ mt: 2, display: "grid", gap: 1.5 }}>
                      <Box
                        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                      >
                        <PersonOutlineIcon color="action" />
                        <Box>
                          <Typography variant="body2">Full Name</Typography>
                          <Typography component="div" sx={{ fontWeight: 600 }}>
                            {profileClient?.name || <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not added yet</Typography>}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                      >
                        <EmailIcon color="action" />
                        <Box>
                          <Typography variant="body2">Email</Typography>
                          <Typography component="div" sx={{ fontWeight: 600 }}>
                            {profileClient?.email || <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not added yet</Typography>}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                      >
                        <LocalPhoneIcon color="action" />
                        <Box>
                          <Typography variant="body2">Phone</Typography>
                          <Typography component="div" sx={{ fontWeight: 600 }}>
                            {profileClient?.phone || <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not added yet</Typography>}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                      >
                        <LocationOnIcon color="action" />
                        <Box>
                          <Typography variant="body2">Address</Typography>
                          <Typography component="div" sx={{ fontWeight: 600 }}>
                            {profileClient?.formattedAddress ||
                              profileClient?.address || <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not added yet</Typography>}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                      >
                        <CalendarTodayIcon color="action" />
                        <Box>
                          <Typography variant="body2">Date of Birth</Typography>
                          <Typography component="div" sx={{ fontWeight: 600 }}>
                            {profileClient?.dob
                              ? formatTimestamp(profileClient.dob)
                              : <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not added yet</Typography>}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                      >
                        <CalendarTodayIcon color="action" />
                        <Box>
                          <Typography variant="body2">Joined</Typography>
                          <Typography component="div" sx={{ fontWeight: 600 }}>
                            {profileClient?.createdAt || profileClient?.created_at || profileClient?.createdOn
                              ? formatTimestamp(profileClient.createdAt || profileClient.created_at || profileClient.createdOn)
                              : "—"}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                      >
                        <CalendarTodayIcon color="action" />
                        <Box>
                          <Typography variant="body2">Updated On</Typography>
                          <Typography component="div" sx={{ fontWeight: 600 }}>
                            {profileClient?.updatedAt || profileClient?.updated_at || profileClient?.updatedOn
                              ? formatTimestamp(profileClient.updatedAt || profileClient.updated_at || profileClient.updatedOn)
                              : "—"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 4 }} sx={{ display: 'flex', minWidth: 0 }}>
                  <Paper sx={{ p: 2, borderRadius: 2,width:'100%', height: '100%', display: 'flex', flexDirection: 'column' }} variant="outlined">
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <Box
                          sx={{
                            bgcolor: "#f5f5f7",
                            p: 1.25,
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <LanguageIcon fontSize="small" />
                        </Box>
                        <Typography variant="h6">Visa Information</Typography>
                      </Box>
                      {profileClient?.verification?.visa?.verified ? (
                        <Chip icon={<VerifiedIcon />} label="Verified" size="small" color="success" />
                      ) : (
                        <Chip icon={<WarningIcon />} label="Unverified" size="small" color="warning" />
                      )}
                    </Box>
                    <Box sx={{ mt: 2, display: "grid", gap: 1.5 }}>
                      <Box
                        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                      >
                        <PublicIcon color="action" />
                        <Box>
                          <Typography variant="body2">
                            Nationality Status
                          </Typography>
                          <Typography component="div" sx={{ fontWeight: 600 }}>
                            {profileClient?.nationalityStatus ||
                              profileClient?.profile?.nationalityStatus || <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not added yet</Typography>}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                      >
                        <LanguageIcon color="action" />
                        <Box>
                          <Typography variant="body2">Visa Category</Typography>
                          <Typography component="div" sx={{ fontWeight: 600 }}>
                            {profileClient?.visaCategory || <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not added yet</Typography>}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                      >
                        <CalendarTodayIcon color="action" />
                        <Box>
                          <Typography variant="body2">Visa Expiry</Typography>
                          <Typography component="div" sx={{ fontWeight: 600 }}>
                            {profileClient?.visaExpiry
                              ? formatTimestamp(profileClient.visaExpiry)
                              : <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not added yet</Typography>}
                          </Typography>
                        </Box>
                      </Box>
                      {profileClient?.verification?.visa?.remark && (
                        <Alert severity="info" icon={<CommentIcon />} sx={{ mt: 1 }}>
                          <Typography variant="caption"><strong>Admin Remark:</strong> {profileClient.verification.visa.remark}</Typography>
                        </Alert>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 4 }} sx={{ display: 'flex', minWidth: 0 }}>
                  <Paper sx={{ p: 2, borderRadius: 2,width:'100%', height: '100%', display: 'flex', flexDirection: 'column' }} variant="outlined">
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <Box
                          sx={{
                            bgcolor: "#f5f5f7",
                            p: 1.25,
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <AccountBalanceIcon fontSize="small" />
                        </Box>
                        <Typography variant="h6">Banking Details</Typography>
                      </Box>
                      {profileClient?.verification?.banking?.verified ? (
                        <Chip icon={<VerifiedIcon />} label="Verified" size="small" color="success" />
                      ) : (
                        <Chip icon={<WarningIcon />} label="Unverified" size="small" color="warning" />
                      )}
                    </Box>
                    <Box sx={{ mt: 2, display: "grid", gap: 1.5 }}>
                      <Box
                        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                      >
                        <AccountBoxIcon color="action" />
                        <Box>
                          <Typography variant="body2">Bank Name</Typography>
                          <Typography component="div" sx={{ fontWeight: 600 }}>
                            {profileClient?.bankName ||
                              profileClient?.bankingDetails?.bankName || <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not added yet</Typography>}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                      >
                        <AccountCircleIcon color="action" />
                        <Box>
                          <Typography variant="body2">Account Name</Typography>
                          <Typography component="div" sx={{ fontWeight: 600 }}>
                            {profileClient?.accountName ||
                              profileClient?.bankingDetails?.accountName || <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not added yet</Typography>}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                      >
                        <CreditCardIcon color="action" />
                        <Box>
                          <Typography variant="body2">BSB</Typography>
                          <Typography component="div" sx={{ fontWeight: 600 }}>
                            {profileClient?.bsb ||
                              profileClient?.bankingDetails?.bsb || <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not added yet</Typography>}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                      >
                        <CreditCardIcon color="action" />
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box>
                            <Typography variant="body2">
                              Account Number
                            </Typography>
                            <Typography component="div" sx={{ fontWeight: 600 }}>
                              {profileClient?.accountNumber ||
                                profileClient?.bankingDetails?.accountNumber || <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not added yet</Typography>}
                            </Typography>
                          </Box>
                          {profileClient?.bankVerified && (
                            <Chip
                              label="Verified"
                              size="small"
                              color="success"
                            />
                          )}
                        </Box>
                      </Box>
                      {profileClient?.verification?.banking?.remark && (
                        <Alert severity="info" icon={<CommentIcon />} sx={{ mt: 1 }}>
                          <Typography variant="caption"><strong>Admin Remark:</strong> {profileClient.verification.banking.remark}</Typography>
                        </Alert>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              <Grid container spacing={2} alignItems="stretch" sx={{ mt: 3 }}>
                <Grid size={{ xs: 12, md: 6, lg: 4 }} sx={{ display: 'flex', minWidth: 0 }}>
                  <Paper sx={{ p: 2, borderRadius: 2,width:'100%', height: '100%', display: 'flex', flexDirection: 'column' }} variant="outlined">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6">Business Information</Typography>
                      {profileClient?.verification?.business?.verified ? (
                        <Chip icon={<VerifiedIcon />} label="Verified" size="small" color="success" />
                      ) : (
                        <Chip icon={<WarningIcon />} label="Unverified" size="small" color="warning" />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Business Name
                    </Typography>
                    <Typography component="div" sx={{ fontWeight: 600 }}>
                      {profileClient?.businessInformation?.businessName ||
                        profileClient?.businessName || <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not added yet</Typography>}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      ABN
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Typography component="div" sx={{ fontWeight: 600 }}>
                        {profileClient?.businessInformation?.abnNumber ||
                          profileClient?.abn || <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not added yet</Typography>}
                      </Typography>
                      <Button size="small" variant="outlined">
                        Verify
                      </Button>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      <Link
                        href={
                          profileClient?.businessInformation?.abnNumber ||
                          profileClient?.abn
                            ? `https://abr.business.gov.au/ABN/View?id=${
                                profileClient?.businessInformation?.abnNumber ||
                                profileClient?.abn
                              }`
                            : "#"
                        }
                        target="_blank"
                        rel="noreferrer"
                        underline="hover"
                      >
                        Verify on ABR website
                      </Link>
                    </Box>
                    {profileClient?.verification?.business?.remark && (
                      <Alert severity="info" icon={<CommentIcon />} sx={{ mt: 2 }}>
                        <Typography variant="caption"><strong>Admin Remark:</strong> {profileClient.verification.business.remark}</Typography>
                      </Alert>
                    )}
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 4 }} sx={{ display: 'flex', minWidth: 0 }}>
                  <Paper sx={{ p: 2, borderRadius: 2,width:'100%', height: '100%', display: 'flex', flexDirection: 'column' }} variant="outlined">
                    <Typography variant="h6">
                      Notification Preferences
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <FormControl component="fieldset">
                        <RadioGroup
                          value={
                            profileClient?.notificationPreference || "email_sms"
                          }
                        >
                          <FormControlLabel
                            value="email"
                            control={<Radio />}
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "center",
                                }}
                              >
                                <EmailIcon color="action" />
                                <Typography>Email Only</Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            value="sms"
                            control={<Radio />}
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "center",
                                }}
                              >
                                <LocalPhoneIcon color="action" />
                                <Typography>SMS Only</Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            value="email_sms"
                            control={<Radio />}
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "center",
                                }}
                              >
                                <NotificationsActiveIcon color="action" />
                                <Typography>
                                  Email & SMS Both (Recommended)
                                </Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            value="none"
                            control={<Radio />}
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "center",
                                }}
                              >
                                <NotificationsActiveIcon color="action" />
                                <Typography>No Notifications</Typography>
                              </Box>
                            }
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12 }} sx={{ display: 'flex', minWidth: 0 }}>
                  <Paper sx={{ p: 2, borderRadius: 2, width:'100%', minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }} variant="outlined">
                    <Typography variant="h6">Documents</Typography>
                    <Box sx={{ mt: 2, flex: 1, minWidth: 0 }}>
                      <ProviderDocumentReviewList
                        providerId={profileClient?.id}
                        providerData={profileClient}
                        legacyDocuments={{
                          ...(profileClient?.documents || {}),
                          ...(profileClient?.provider_onboarding?.documents || {}),
                        }}
                        embedded
                        showHeader={false}
                      />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DescriptionIcon />
                    <Typography variant="h6">About Us</Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color={providerAboutUsText ? "text.secondary" : "text.disabled"}
                    sx={{
                      mt: 1.5,
                      whiteSpace: "pre-line",
                      fontStyle: providerAboutUsText ? "normal" : "italic",
                    }}
                  >
                    {providerAboutUsText || "Not added yet"}
                  </Typography>
                </Paper>
              </Box>

              {/* Provider Services Section */}
              <Box sx={{ mt: 3 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LocalOfferIcon />
                      <Typography variant="h6">
                        Provider Services
                        {providerServices && providerServices.length
                          ? ` (${providerServices.length})`
                          : ""}
                      </Typography>
                    </Box>
                    {/* action buttons could be added here */}
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    {servicesLoading ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 4,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : providerServices && providerServices.length > 0 ? (
                      <Box sx={{ display: "grid", gap: 2 }}>
                        {providerServices.map((s, index) => {
                          const normalizedService =
                            normalizeProviderService(s, index) || s;
                          const serviceImage = resolveServiceImageUrl(
                            normalizedService
                          );
                          const serviceTitle =
                            normalizedService.title ||
                            normalizedService.name ||
                            "Service";
                          const servicePriceLabel = formatServicePrice(
                            normalizedService.price
                          );
                          const serviceDurationLabel = formatServiceDuration(
                            normalizedService.duration
                          );

                          return (
                            <Paper
                              key={normalizedService.id || `${serviceTitle}-${index}`}
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                display: "flex",
                                gap: 2,
                                alignItems: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 84,
                                  height: 64,
                                  bgcolor: "#f5f5f7",
                                  borderRadius: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {serviceImage ? (
                                  <img
                                    src={serviceImage}
                                    alt={serviceTitle}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      borderRadius: 6,
                                    }}
                                  />
                                ) : (
                                  <ImageIcon />
                                )}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 700 }}>
                                  {serviceTitle}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {normalizedService.description ||
                                    normalizedService.subtitle ||
                                    ""}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 2,
                                    mt: 1,
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 0.5,
                                      alignItems: "center",
                                    }}
                                  >
                                    <AttachMoneyIcon fontSize="small" />{" "}
                                    <Typography variant="body2">
                                      {servicePriceLabel}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 0.5,
                                      alignItems: "center",
                                    }}
                                  >
                                    <AccessTimeIcon fontSize="small" />{" "}
                                    <Typography variant="body2">
                                      {serviceDurationLabel}
                                    </Typography>
                                  </Box>
                                  {normalizedService.category && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 0.5,
                                        alignItems: "center",
                                      }}
                                    >
                                      <LocalOfferIcon fontSize="small" />{" "}
                                      <Typography variant="body2">
                                        {normalizedService.category}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                              <Chip
                                label={normalizedService.active ? "ACTIVE" : "INACTIVE"}
                                color={normalizedService.active ? "success" : "default"}
                              />
                            </Paper>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: "center", py: 6 }}>
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            bgcolor: "#f5f5f7",
                            borderRadius: "50%",
                            mx: "auto",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <LocalOfferIcon />
                        </Box>
                        <Typography variant="h6" sx={{ mt: 2 }}>
                          No Services Found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          This provider has not added any services yet.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setProfileDialogOpen(false);
              setProfileClient(null);
              setProviderServices([]);
              setServicesLoading(false);
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setEditingClient(profileClient);
              setFormOpen(true);
              setProfileDialogOpen(false);
              setProfileClient(null);
              setProviderServices([]);
              setServicesLoading(false);
            }}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Client Form Dialog */}
      <UserForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingClient(null);
        }}
        onSubmit={handleFormSubmit}
        user={editingClient}
        userType="client"
        loading={formLoading}
      />

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreview.open}
        onClose={handleCloseImagePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{imagePreview.title}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              href={imagePreview.url}
              target="_blank"
              rel="noreferrer"
            >
              Open in New Tab
            </Button>
            <Button size="small" onClick={handleCloseImagePreview}>
              Close
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
            }}
          >
            <img
              src={imagePreview.url}
              alt={imagePreview.title}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog for activate/deactivate */}
      <Dialog
        open={accountConfirmOpen}
        onClose={() => { setAccountConfirmOpen(false); setAccountPendingValue(null); }}
        aria-labelledby="account-confirm-dialog-title"
      >
        <DialogTitle id="account-confirm-dialog-title">Confirm action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {accountPendingValue
              ? "Are you sure you want to activate this provider role?"
              : "Are you sure you want to deactivate this provider role? Deactivated providers will be hidden from customers and will not receive bookings, leads, emails or push notifications."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAccountConfirmOpen(false); setAccountPendingValue(null); }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAccountToggle} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Clients;
